import { useEffect, useRef } from 'react';
import type { ReclamationFormValues } from '../../data/reclamaciones';
import type { SubmissionResult } from '../../lib/reclamaciones';

function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="complaints-review__row">
      <dt>{label}</dt>
      <dd>{value === null || value === undefined || value === '' ? 'No consignado' : value}</dd>
    </div>
  );
}

function formatBytes(bytes: number) {
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function ReclamationReview({
  values,
  files,
  submitting,
  submitError,
  result,
  onBack,
  onConfirm,
  onNew,
}: {
  values: ReclamationFormValues;
  files: File[];
  submitting: boolean;
  submitError: string;
  result: SubmissionResult | null;
  onBack: () => void;
  onConfirm: () => void;
  onNew: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, [result]);

  if (result) {
    const date = new Intl.DateTimeFormat('es-PE', {
      dateStyle: 'long',
      timeStyle: 'medium',
      timeZone: 'America/Lima',
    }).format(new Date(result.fechaRegistro));

    return (
      <section className="complaints-success" aria-live="polite">
        <span className="complaints-success__mark" aria-hidden="true">✓</span>
        <p className="eyebrow">Registro completado</p>
        <h2 ref={headingRef} tabIndex={-1}>Tu hoja fue registrada correctamente</h2>
        <p>Conserva este código como constancia y referencia para el seguimiento.</p>
        <div className="complaints-success__code">{result.codigo}</div>
        <dl>
          <Row label="Fecha y hora" value={date} />
          <Row label="Notificación interna" value={result.correoNotificacionEnviado ? 'Enviada' : 'Pendiente por incidencia de correo'} />
          <Row label="Constancia al consumidor" value={result.correoConstanciaEnviado ? 'Enviada' : 'Pendiente por incidencia de correo'} />
        </dl>
        <p className="complaints-alert complaints-alert--info">
          El registro permanece guardado en la base de datos aunque alguno de los correos figure pendiente.
        </p>
        <div className="complaints-actions">
          <button type="button" className="complaints-button complaints-button--secondary" onClick={() => window.print()}>
            Imprimir constancia
          </button>
          <button type="button" className="complaints-button" onClick={onNew}>Registrar otra hoja</button>
        </div>
      </section>
    );
  }

  const amount = values.montoReclamado
    ? `${values.moneda === 'PEN' ? 'S/' : 'US$'} ${values.montoReclamado}`
    : 'No consignado';

  return (
    <section className="complaints-review" aria-busy={submitting || undefined}>
      <p className="eyebrow">Paso final</p>
      <h2 ref={headingRef} tabIndex={-1}>Revisa la información antes de enviar</h2>
      <p>Comprueba que los datos sean correctos. El número correlativo será generado únicamente por el servidor.</p>

      <div className="complaints-review__section">
        <h3>Consumidor</h3>
        <dl>
          <Row label="Nombre" value={`${values.nombres} ${values.apellidos}`} />
          <Row label="Documento" value={`${values.tipoDocumento}: ${values.numeroDocumento}`} />
          <Row label="Correo" value={values.correo} />
          <Row label="Teléfono" value={values.telefono} />
          <Row label="Domicilio" value={`${values.direccion}, ${values.distrito}, ${values.provincia}, ${values.departamento}`} />
          <Row label="UBIGEO INEI" value={values.ubigeo} />
        </dl>
      </div>

      {values.esMenorEdad ? (
        <div className="complaints-review__section">
          <h3>Representante</h3>
          <dl>
            <Row label="Nombre" value={values.nombresRepresentante} />
            <Row label="Documento" value={`${values.tipoDocumentoRepresentante}: ${values.numeroDocumentoRepresentante}`} />
            <Row label="Correo" value={values.correoRepresentante} />
            <Row label="Teléfono" value={values.telefonoRepresentante} />
            <Row label="Domicilio" value={values.direccionRepresentante} />
          </dl>
        </div>
      ) : null}

      <div className="complaints-review__section">
        <h3>Producto o servicio</h3>
        <dl>
          <Row label="Tipo" value={values.tipoBien} />
          <Row label="Descripción" value={values.descripcionBienServicio} />
          <Row label="Fecha" value={values.fechaAdquisicion} />
          <Row label="Monto reclamado" value={amount} />
          <Row label="Comprobante" value={`${values.tipoComprobante}${values.numeroComprobante ? `: ${values.numeroComprobante}` : ''}`} />
        </dl>
      </div>

      <div className="complaints-review__section">
        <h3>{values.tipoRegistro}</h3>
        <dl>
          <Row label="Detalle" value={values.detalle} />
          <Row label="Pedido" value={values.pedido} />
          <Row label="Medio de respuesta" value={values.medioRespuesta} />
        </dl>
      </div>

      <div className="complaints-review__section">
        <h3>Adjuntos</h3>
        {files.length ? (
          <ul className="complaints-file-list">
            {files.map((file) => <li key={`${file.name}-${file.lastModified}`}>{file.name} <span>{formatBytes(file.size)}</span></li>)}
          </ul>
        ) : <p>No se adjuntaron archivos.</p>}
      </div>

      {submitError ? <p className="complaints-alert complaints-alert--error" role="alert">{submitError}</p> : null}

      <div className="complaints-actions">
        <button type="button" className="complaints-button complaints-button--secondary" onClick={onBack} disabled={submitting}>Volver y corregir</button>
        <button type="button" className="complaints-button" onClick={onConfirm} disabled={submitting}>
          {submitting ? 'Registrando…' : 'Enviar y registrar'}
        </button>
      </div>
    </section>
  );
}
